import React from 'react';
import { FormDefinitionField, FormItem } from '@/interfaces/api/models';
import { FieldProps, parseJsonSafely, SignaturePreview } from './form-helpers';
import FormFieldText from './Fields/FormFieldText';
import FormFieldLongText from './Fields/FormFieldLongText';
import FormFieldInformationalText from './Fields/FormFieldInformationalText';
import FormFieldNumber from './Fields/FormFieldNumber';
import FormFieldCheckbox from './Fields/FormFieldCheckbox';
import FormFieldBoolean from './Fields/FormFieldBoolean';
import FormFieldDate from './Fields/FormFieldDate';
import FormFieldDateTime from './Fields/FormFieldDateTime';
import FormFieldSelect from './Fields/FormFieldSelect';
import FormFieldMultiSelect from './Fields/FormFieldMultiSelect';
import FormFieldImage from './Fields/FormFieldImage';
import FormFieldInformationalImage from './Fields/FormFieldInformationalImage';
import FormFieldSignature from './Fields/FormFieldSignature';
import FormFieldTable from './Table/FormFieldTable';

export interface FormFieldRendererProps extends FieldProps {
    field: FormDefinitionField;
    availableOptions?: string[];
    onUpload?: (file: File) => Promise<string>;
    resolveImageSrc?: (attachmentID: string) => string;
    signatureContext?: { ensurePersisted: () => Promise<void>; itemID: string };
    // The remaining props only matter for `Table`, whose cells are rendered through this same
    // component and therefore need the *resolvers* rather than one field's already-resolved values.
    sectionGroup?: number;
    resolveFieldOptions?: (field: FormDefinitionField) => string[];
    buildSignatureContext?: (item: FormItem) => { ensurePersisted: () => Promise<void>; itemID: string };
    // The form's whole error map, so a table can find its per-cell errors (keyed by
    // `getTableCellID`, not by the table item's own ID).
    cellErrors?: Record<string, string>;
    // `form.clearFieldError` — a table cell's synthetic ID is unknown to `@mantine/form`'s own
    // built-in "clear this field's error on change" behaviour (that only clears the exact path
    // `setFieldValue` was called with, i.e. the table's own item ID), so a cell has to clear its
    // own error explicitly when edited, the same way every non-table field's error already does.
    onClearCellError?: (path: string) => void;
    resolveSignaturePreview?: (signatureID: string) => SignaturePreview | null;
    onSignatureSaved?: (signature: any) => void;
}

function FormFieldRenderer({ field, value, onChange, onBlur, error, disabled, availableOptions, onUpload, resolveImageSrc, signatureContext, sectionGroup, resolveFieldOptions, buildSignatureContext, cellErrors, onClearCellError, resolveSignaturePreview, onSignatureSaved, inTable }: FormFieldRendererProps) {
    switch (field.DataType) {
        case 'String':
            return <FormFieldText label={field.Description} required={field.Required} value={value as string} onChange={onChange} onBlur={onBlur} error={error} disabled={disabled} inTable={inTable} />;
        case 'LongText':
            return <FormFieldLongText label={field.Description} required={field.Required} value={value as string} onChange={onChange} onBlur={onBlur} error={error} disabled={disabled} inTable={inTable} />;
        case 'InformationalText':
            return <FormFieldInformationalText label={field.Description} />;
        case 'Number':
            return <FormFieldNumber label={field.Description} required={field.Required} value={value as number | null} onChange={onChange} onBlur={onBlur} error={error} disabled={disabled} inTable={inTable} />;
        case 'Checkbox':
            return <FormFieldCheckbox label={field.Description} value={value as boolean} onChange={onChange} disabled={disabled} />;
        case 'Boolean':
            return <FormFieldBoolean label={field.Description} required={field.Required} value={value as string} onChange={onChange} error={error} disabled={disabled} inTable={inTable} />;
        case 'Date':
            return <FormFieldDate label={field.Description} required={field.Required} value={value as Date | null} onChange={onChange} onBlur={onBlur} error={error} disabled={disabled} inTable={inTable} />;
        case 'DateTime':
            return <FormFieldDateTime label={field.Description} required={field.Required} value={value as Date | null} onChange={onChange} onBlur={onBlur} error={error} disabled={disabled} inTable={inTable} />;
        case 'Select':
            return <FormFieldSelect label={field.Description} required={field.Required} value={value as string} onChange={onChange} availableOptions={availableOptions ?? []} error={error} disabled={disabled} inTable={inTable} />;
        case 'MultiSelect':
            return <FormFieldMultiSelect label={field.Description} required={field.Required} value={value as string[]} onChange={onChange} availableOptions={availableOptions ?? []} error={error} disabled={disabled} inTable={inTable} />;
        case 'Image':
            return <FormFieldImage label={field.Description} required={field.Required} value={value as string | null} onChange={onChange} onUpload={onUpload} resolveImageSrc={resolveImageSrc ?? (() => '')} error={error} disabled={disabled} />;
        case 'InformationalImage':
            return <FormFieldInformationalImage attachmentID={field.Description ?? null} resolveImageSrc={resolveImageSrc ?? (() => '')} />;
        case 'Signature': {
            if (!signatureContext) return null;
            return (
                <FormFieldSignature
                    label={field.Description}
                    value={value as string | null}
                    onChange={onChange}
                    ensurePersisted={signatureContext.ensurePersisted}
                    signatureTemplateID={parseJsonSafely<{ SignatureTemplateID?: string }>(field.DataOption, {}).SignatureTemplateID ?? ''}
                    itemID={signatureContext.itemID}
                    disabled={disabled}
                    error={error}
                    resolveSignaturePreview={resolveSignaturePreview}
                    onSignatureSaved={onSignatureSaved}
                />
            );
        }
        case 'Table':
            return (
                <FormFieldTable
                    field={field}
                    value={value as string | null}
                    onChange={onChange as (v: string) => void}
                    sectionGroup={sectionGroup}
                    disabled={disabled}
                    resolveFieldOptions={resolveFieldOptions}
                    onUpload={onUpload}
                    resolveImageSrc={resolveImageSrc}
                    buildSignatureContext={buildSignatureContext}
                    cellErrors={cellErrors}
                    onClearCellError={onClearCellError}
                    resolveSignaturePreview={resolveSignaturePreview}
                    onSignatureSaved={onSignatureSaved}
                />
            );
        default:
            return null; // remaining DataTypes are wired in Tasks 10-14
    }
}

export default React.memo(FormFieldRenderer);
